/**
 * VersionCompare - Enhanced Version Compare (MA-COMPARE-002)
 * 
 * Upgrades MA-COMPARE-001 with:
 * - Semantic change descriptions
 * - Side-by-side step comparison
 * - Grouped diff presentation  
 * - Graph overlay visualization mode
 * - Better AI change transparency
 * 
 * Data source: /api/funnels/{id}/compare/{base}/{compare}
 */

import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  GitCompare,
  Plus,
  Minus,
  Pencil,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  Loader2,
  AlertCircle,
  CircleDot,
  Link2,
  Unlink,
  RefreshCw,
  Sparkles,
  MessageSquare,
  HelpCircle,
  TextCursorInput,
  Shield,
  Calendar,
  GitBranch,
  Eye,
  SplitSquareVertical,
  Layers,
  FileText,
  Zap,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';

const API_URL = window.location.origin;

// =============================================================================
// Constants & Config
// =============================================================================

const CHANGE_STYLES = {
  added: {
    icon: Plus,
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/30',
    label: 'Added',
    ringColor: 'ring-emerald-500/50',
  },
  removed: {
    icon: Minus,
    color: 'text-red-400',
    bg: 'bg-red-500/10',
    border: 'border-red-500/30',
    label: 'Removed',
    ringColor: 'ring-red-500/50',
  },
  modified: {
    icon: Pencil,
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/30',
    label: 'Modified',
    ringColor: 'ring-amber-500/50',
  },
};

const NODE_TYPE_CONFIG = {
  message: { icon: MessageSquare, label: 'Message', color: 'text-indigo-400' },
  question: { icon: HelpCircle, label: 'Question', color: 'text-indigo-400' },
  response: { icon: MessageSquare, label: 'Response', color: 'text-indigo-400' },
  input: { icon: TextCursorInput, label: 'Input', color: 'text-purple-400' },
  consent: { icon: Shield, label: 'Consent', color: 'text-amber-400' },
  booking: { icon: Calendar, label: 'Booking', color: 'text-blue-400' },
  condition: { icon: GitBranch, label: 'Condition', color: 'text-orange-400' },
};

// =============================================================================
// Semantic Change Descriptions (MA-COMPARE-002)
// =============================================================================

/**
 * Generate human-readable semantic description for a node change
 */
function getSemanticDescription(change) {
  const nodeType = change.node_type || 'step';
  const typeLabel = NODE_TYPE_CONFIG[nodeType]?.label || nodeType;
  const label = change.label || change.node_id;
  
  if (change.change_type === 'added') {
    if (nodeType === 'question') return `New qualification question added: "${label}"`;
    if (nodeType === 'booking') return 'Booking step added to funnel';
    if (nodeType === 'consent') return 'Consent step added for compliance';
    if (nodeType === 'input') return `Input field added: "${label}"`;
    return `${typeLabel} step added: "${label}"`;
  }
  
  if (change.change_type === 'removed') {
    if (nodeType === 'question') return `Question removed: "${label}"`;
    if (nodeType === 'booking') return 'Booking step removed from funnel';
    return `${typeLabel} step removed: "${label}"`;
  }
  
  if (change.change_type === 'modified') {
    const fieldChanges = change.field_changes || [];
    const contentChange = fieldChanges.find(f => f.field === 'content');
    
    if (contentChange?.changes) {
      const msgChange = contentChange.changes.find(c => c.field === 'message');
      if (msgChange) {
        if (nodeType === 'question') return 'Question wording updated';
        if (nodeType === 'booking') return 'Booking CTA strengthened';
        return `${typeLabel} message updated`;
      }
      
      const responsesChange = contentChange.changes.find(c => c.field === 'responses');
      if (responsesChange) return 'Response options changed';
    }
    
    const labelChange = fieldChanges.find(f => f.field === 'metadata')?.changes?.find(c => c.field === 'label');
    if (labelChange) return `Step renamed to "${labelChange.after}"`;
    
    return `${typeLabel} content modified`;
  }
  
  return `${typeLabel} changed`;
}

/**
 * Generate semantic description for edge change
 */
function getEdgeSemanticDescription(change, nodeLabels = {}) {
  const getLabel = (id) => nodeLabels[id] || id;
  
  if (change.change_type === 'added') {
    return `New route: ${getLabel(change.source_node_id)} → ${getLabel(change.target_node_id)}`;
  }
  
  if (change.change_type === 'removed') {
    return `Route removed: ${getLabel(change.source_node_id)} → ${getLabel(change.target_node_id)}`;
  }
  
  if (change.change_type === 'modified') {
    if (change.before?.target_node_id !== change.after?.target_node_id) {
      return `Routing changed: ${getLabel(change.source_node_id)} now goes to ${getLabel(change.after?.target_node_id)}`;
    }
    if (change.before?.condition !== change.after?.condition) {
      return `Routing condition updated for ${getLabel(change.source_node_id)}`;
    }
    return `Route modified: ${getLabel(change.source_node_id)} → ${getLabel(change.target_node_id)}`;
  }
  
  return change.description || 'Route changed';
}

// =============================================================================
// API Hook
// =============================================================================

async function fetchVersionDiff(funnelId, baseVersion, compareVersion, token) {
  const res = await fetch(
    `${API_URL}/api/funnels/${funnelId}/compare/${baseVersion}/${compareVersion}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );
  
  if (!res.ok) throw new Error('Failed to fetch version diff');
  return res.json();
}

function useVersionDiff(funnelId, baseVersion, compareVersion, enabled = true) {
  const token = localStorage.getItem('scriptcompass_token');
  
  return useQuery({
    queryKey: ['versionDiff', funnelId, baseVersion, compareVersion],
    queryFn: () => fetchVersionDiff(funnelId, baseVersion, compareVersion, token),
    enabled: enabled && !!funnelId && baseVersion !== null && compareVersion !== null,
    staleTime: 30000,
  });
}

// =============================================================================
// Summary Component (Enhanced)
// =============================================================================

function DiffSummary({ summary }) {
  const totalChanges = summary.total_changes || (
    summary.nodes_added + summary.nodes_removed + summary.nodes_modified +
    summary.edges_added + summary.edges_removed + summary.edges_modified +
    (summary.entry_node_changed ? 1 : 0)
  );
  
  if (totalChanges === 0) {
    return (
      <div className="text-sm text-slate-400 flex items-center gap-2">
        <CircleDot className="h-4 w-4" />
        No structural changes detected
      </div>
    );
  }
  
  const groups = [
    { 
      label: 'Steps', 
      added: summary.nodes_added, 
      removed: summary.nodes_removed, 
      modified: summary.nodes_modified 
    },
    { 
      label: 'Routes', 
      added: summary.edges_added, 
      removed: summary.edges_removed, 
      modified: summary.edges_modified 
    },
  ];
  
  return (
    <div className="space-y-3">
      {/* Total badge */}
      <div className="flex items-center gap-2">
        <Badge className="bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
          <Zap className="h-3 w-3 mr-1" />
          {totalChanges} change{totalChanges !== 1 ? 's' : ''} detected
        </Badge>
        {summary.entry_node_changed && (
          <Badge className="bg-purple-500/20 text-purple-300 border border-purple-500/30">
            <CircleDot className="h-3 w-3 mr-1" />
            Entry changed
          </Badge>
        )}
      </div>
      
      {/* Grouped counts */}
      <div className="flex gap-4 text-xs">
        {groups.map(group => (
          <div key={group.label} className="flex items-center gap-2">
            <span className="text-slate-500">{group.label}:</span>
            {group.added > 0 && (
              <span className="text-emerald-400">+{group.added}</span>
            )}
            {group.removed > 0 && (
              <span className="text-red-400">-{group.removed}</span>
            )}
            {group.modified > 0 && (
              <span className="text-amber-400">~{group.modified}</span>
            )}
            {group.added === 0 && group.removed === 0 && group.modified === 0 && (
              <span className="text-slate-600">0</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// Side-by-Side Comparison (MA-COMPARE-002)
// =============================================================================

function SideBySideCompare({ before, after, field = 'content' }) {
  const beforeContent = before?.[field];
  const afterContent = after?.[field];
  
  if (!beforeContent && !afterContent) return null;
  
  return (
    <div className="grid grid-cols-2 gap-3 mt-3">
      {/* Before */}
      <div className="rounded-lg border border-red-500/20 bg-red-500/5 p-3">
        <p className="text-[10px] text-red-400 font-medium mb-2 uppercase tracking-wide">Before</p>
        <ContentDisplay content={beforeContent} className="text-slate-400" />
      </div>
      
      {/* After */}
      <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3">
        <p className="text-[10px] text-emerald-400 font-medium mb-2 uppercase tracking-wide">After</p>
        <ContentDisplay content={afterContent} className="text-slate-200" />
      </div>
    </div>
  );
}

function ContentDisplay({ content, className }) {
  if (!content) return <span className="text-slate-600 italic">Empty</span>;
  
  // Handle message content
  if (content.message) {
    return (
      <div className={cn("space-y-2", className)}>
        <p className="text-sm">{content.message}</p>
        {content.responses && (
          <div className="space-y-1 mt-2 pt-2 border-t border-white/5">
            {content.responses.map((r, i) => (
              <div key={i} className="text-xs flex items-center gap-2">
                <span className="w-4 h-4 rounded bg-white/5 flex items-center justify-center text-[10px]">
                  {i + 1}
                </span>
                {r.label}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
  
  // Handle other content types
  return (
    <div className={cn("text-xs space-y-1", className)}>
      {Object.entries(content).map(([key, value]) => (
        <div key={key}>
          <span className="text-slate-500">{key}: </span>
          <span>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// Node Change Card (Enhanced)
// =============================================================================

function NodeChangeCard({ change, isExpanded, onToggle }) {
  const style = CHANGE_STYLES[change.change_type];
  const Icon = style.icon;
  const typeConfig = NODE_TYPE_CONFIG[change.node_type] || { icon: CircleDot, label: change.node_type, color: 'text-slate-400' };
  const TypeIcon = typeConfig.icon;
  
  const semanticDesc = getSemanticDescription(change);
  const isAiGenerated = change.after?.metadata?.ai_generated || change.before?.metadata?.ai_generated;
  
  return (
    <Collapsible open={isExpanded} onOpenChange={onToggle}>
      <div className={cn(
        "rounded-lg border p-4 mb-3 transition-all",
        style.bg,
        style.border,
        isExpanded && "ring-1",
        isExpanded && style.ringColor
      )}>
        <CollapsibleTrigger asChild>
          <button className="w-full flex items-start justify-between text-left group">
            <div className="flex items-start gap-3 flex-1">
              {/* Change Type Icon */}
              <div className={cn("p-2 rounded-lg", style.bg)}>
                <Icon className={cn("h-4 w-4", style.color)} />
              </div>
              
              <div className="flex-1 min-w-0">
                {/* Semantic Description */}
                <p className={cn("font-medium text-sm", style.color)}>
                  {semanticDesc}
                </p>
                
                {/* Badges row */}
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  <Badge variant="outline" className={cn("text-[10px] border-white/10", typeConfig.color)}>
                    <TypeIcon className="h-2.5 w-2.5 mr-1" />
                    {typeConfig.label}
                  </Badge>
                  
                  {isAiGenerated && (
                    <Badge variant="outline" className="text-[10px] border-purple-500/30 text-purple-400">
                      <Sparkles className="h-2.5 w-2.5 mr-1" />
                      AI Generated
                    </Badge>
                  )}
                </div>
                
                {/* Node ID */}
                <p className="text-[10px] text-slate-500 mt-1 font-mono">
                  {change.node_id}
                </p>
              </div>
            </div>
            
            <ChevronDown className={cn(
              "h-4 w-4 text-slate-400 transition-transform group-hover:text-slate-200",
              isExpanded && "rotate-180"
            )} />
          </button>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <div className="mt-4 pt-4 border-t border-white/5">
            {/* Side-by-side for modifications */}
            {change.change_type === 'modified' && (
              <SideBySideCompare before={change.before} after={change.after} />
            )}
            
            {/* Content preview for added */}
            {change.change_type === 'added' && change.after?.content && (
              <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3">
                <p className="text-[10px] text-emerald-400 font-medium mb-2 uppercase tracking-wide">New Content</p>
                <ContentDisplay content={change.after.content} className="text-slate-200" />
              </div>
            )}
            
            {/* Content preview for removed */}
            {change.change_type === 'removed' && change.before?.content && (
              <div className="rounded-lg border border-red-500/20 bg-red-500/5 p-3">
                <p className="text-[10px] text-red-400 font-medium mb-2 uppercase tracking-wide">Removed Content</p>
                <ContentDisplay content={change.before.content} className="text-slate-400 line-through" />
              </div>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

// =============================================================================
// Edge Change Card
// =============================================================================

function EdgeChangeCard({ change, nodeLabels }) {
  const style = CHANGE_STYLES[change.change_type];
  const Icon = change.change_type === 'modified' ? RefreshCw : 
               change.change_type === 'added' ? Link2 : Unlink;
  
  const semanticDesc = getEdgeSemanticDescription(change, nodeLabels);
  
  return (
    <div className={cn(
      "rounded-lg border p-3 mb-2",
      style.bg,
      style.border
    )}>
      <div className="flex items-start gap-3">
        <div className={cn("p-1.5 rounded-md", style.bg)}>
          <Icon className={cn("h-4 w-4", style.color)} />
        </div>
        
        <div className="flex-1 min-w-0">
          <p className={cn("font-medium text-sm", style.color)}>
            {semanticDesc}
          </p>
          
          {change.change_type === 'modified' && change.before && change.after && (
            <div className="flex items-center gap-2 mt-2 text-xs">
              <span className="text-red-400 line-through">
                {nodeLabels[change.before.target_node_id] || change.before.target_node_id}
              </span>
              <ArrowRight className="h-3 w-3 text-slate-500" />
              <span className="text-emerald-400">
                {nodeLabels[change.after.target_node_id] || change.after.target_node_id}
              </span>
            </div>
          )}
          
          <p className="text-[10px] text-slate-500 mt-1 font-mono">
            {change.edge_id}
          </p>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Entry Node Change
// =============================================================================

function EntryNodeChange({ entryNode, nodeLabels }) {
  if (!entryNode?.changed) return null;
  
  return (
    <div className="rounded-lg border p-4 mb-3 bg-purple-500/10 border-purple-500/30">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-purple-500/20">
          <CircleDot className="h-4 w-4 text-purple-400" />
        </div>
        
        <div>
          <p className="font-medium text-sm text-purple-400">
            Entry point changed
          </p>
          <p className="text-sm text-slate-400 mt-1">
            Funnel now starts at a different step
          </p>
          
          <div className="flex items-center gap-2 mt-2 text-sm">
            <span className="text-red-400 line-through">
              {nodeLabels[entryNode.before] || entryNode.before || '(none)'}
            </span>
            <ArrowRight className="h-3 w-3 text-slate-500" />
            <span className="text-emerald-400">
              {nodeLabels[entryNode.after] || entryNode.after || '(none)'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Graph Overlay Visualization (MA-COMPARE-002)
// =============================================================================

function GraphOverlay({ diff, nodeLabels }) {
  const { node_changes = [], edge_changes = [] } = diff;
  
  // Group nodes by change type
  const addedNodes = node_changes.filter(n => n.change_type === 'added');
  const removedNodes = node_changes.filter(n => n.change_type === 'removed');
  const modifiedNodes = node_changes.filter(n => n.change_type === 'modified');
  
  if (node_changes.length === 0 && edge_changes.length === 0 && !diff.entry_node?.changed) {
    return (
      <div className="flex items-center justify-center h-48 text-slate-500">
        <CircleDot className="h-5 w-5 mr-2" />
        No changes to visualize
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Added Nodes */}
      {addedNodes.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-medium text-emerald-400 flex items-center gap-2">
            <Plus className="h-3 w-3" />
            Added Steps ({addedNodes.length})
          </h4>
          <div className="flex flex-wrap gap-2">
            {addedNodes.map(node => {
              const config = NODE_TYPE_CONFIG[node.node_type] || { icon: CircleDot, color: 'text-slate-400' };
              const Icon = config.icon;
              return (
                <div 
                  key={node.node_id}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10"
                >
                  <Icon className={cn("h-4 w-4", config.color)} />
                  <span className="text-sm text-emerald-300">{node.label || node.node_id}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Removed Nodes */}
      {removedNodes.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-medium text-red-400 flex items-center gap-2">
            <Minus className="h-3 w-3" />
            Removed Steps ({removedNodes.length})
          </h4>
          <div className="flex flex-wrap gap-2">
            {removedNodes.map(node => {
              const config = NODE_TYPE_CONFIG[node.node_type] || { icon: CircleDot, color: 'text-slate-400' };
              const Icon = config.icon;
              return (
                <div 
                  key={node.node_id}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border border-red-500/30 bg-red-500/10 opacity-60"
                >
                  <Icon className={cn("h-4 w-4", config.color)} />
                  <span className="text-sm text-red-300 line-through">{node.label || node.node_id}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Modified Nodes */}
      {modifiedNodes.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-medium text-amber-400 flex items-center gap-2">
            <Pencil className="h-3 w-3" />
            Modified Steps ({modifiedNodes.length})
          </h4>
          <div className="flex flex-wrap gap-2">
            {modifiedNodes.map(node => {
              const config = NODE_TYPE_CONFIG[node.node_type] || { icon: CircleDot, color: 'text-slate-400' };
              const Icon = config.icon;
              return (
                <div 
                  key={node.node_id}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-500/30 bg-amber-500/10"
                >
                  <Icon className={cn("h-4 w-4", config.color)} />
                  <span className="text-sm text-amber-300">{node.label || node.node_id}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Routing Changes */}
      {edge_changes.length > 0 && (
        <div className="space-y-2 pt-4 border-t border-white/5">
          <h4 className="text-xs font-medium text-indigo-400 flex items-center gap-2">
            <GitBranch className="h-3 w-3" />
            Routing Changes ({edge_changes.length})
          </h4>
          <div className="space-y-1">
            {edge_changes.map(edge => (
              <div 
                key={edge.edge_id}
                className={cn(
                  "text-xs px-3 py-2 rounded flex items-center gap-2",
                  edge.change_type === 'added' && "bg-emerald-500/10 text-emerald-300",
                  edge.change_type === 'removed' && "bg-red-500/10 text-red-300",
                  edge.change_type === 'modified' && "bg-amber-500/10 text-amber-300"
                )}
              >
                {edge.change_type === 'added' && <Plus className="h-3 w-3" />}
                {edge.change_type === 'removed' && <Minus className="h-3 w-3" />}
                {edge.change_type === 'modified' && <RefreshCw className="h-3 w-3" />}
                <span>{nodeLabels[edge.source_node_id] || edge.source_node_id}</span>
                <ArrowRight className="h-3 w-3" />
                <span>{nodeLabels[edge.target_node_id] || edge.target_node_id}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Main Compare Dialog (Enhanced)
// =============================================================================

export function VersionCompareDialog({ 
  funnelId, 
  baseVersion, 
  compareVersion,
  trigger,
  open,
  onOpenChange,
}) {
  const [activeTab, setActiveTab] = useState('list');
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  
  const { data: diff, isLoading, error } = useVersionDiff(
    funnelId, 
    baseVersion, 
    compareVersion,
    open
  );
  
  // Build node labels map
  const nodeLabels = useMemo(() => {
    if (!diff) return {};
    const labels = {};
    
    diff.node_changes?.forEach(change => {
      const label = change.label || change.after?.metadata?.label || change.before?.metadata?.label;
      if (label) labels[change.node_id] = label;
    });
    
    return labels;
  }, [diff]);
  
  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };
  
  const expandAll = () => {
    if (diff?.node_changes) {
      setExpandedNodes(new Set(diff.node_changes.map(n => n.node_id)));
    }
  };
  
  const collapseAll = () => {
    setExpandedNodes(new Set());
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden bg-slate-900 border-white/10">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-100">
            <GitCompare className="h-5 w-5 text-indigo-400" />
            Version Compare
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Comparing version {baseVersion} → {compareVersion}
          </DialogDescription>
        </DialogHeader>
        
        {isLoading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="h-6 w-6 text-slate-400 animate-spin" />
            <span className="ml-2 text-slate-400">Analyzing changes...</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-48 text-red-400">
            <AlertCircle className="h-8 w-8 mb-2" />
            <p className="text-sm">Failed to load version comparison</p>
          </div>
        ) : diff ? (
          <div className="space-y-4">
            {/* Summary */}
            <div className="p-4 rounded-lg bg-white/[0.02] border border-white/5">
              <DiffSummary summary={diff.summary} />
            </div>
            
            {/* View Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <div className="flex items-center justify-between">
                <TabsList className="bg-slate-800/50">
                  <TabsTrigger value="list" className="gap-2 data-[state=active]:bg-indigo-500/20">
                    <FileText className="h-3.5 w-3.5" />
                    Detailed List
                  </TabsTrigger>
                  <TabsTrigger value="sidebyside" className="gap-2 data-[state=active]:bg-indigo-500/20">
                    <SplitSquareVertical className="h-3.5 w-3.5" />
                    Side-by-Side
                  </TabsTrigger>
                  <TabsTrigger value="graph" className="gap-2 data-[state=active]:bg-indigo-500/20">
                    <Layers className="h-3.5 w-3.5" />
                    Graph View
                  </TabsTrigger>
                </TabsList>
                
                {activeTab === 'list' && (
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={expandAll} className="h-7 text-xs text-slate-400">
                      Expand All
                    </Button>
                    <Button variant="ghost" size="sm" onClick={collapseAll} className="h-7 text-xs text-slate-400">
                      Collapse All
                    </Button>
                  </div>
                )}
              </div>
              
              <ScrollArea className="h-[50vh] mt-4">
                <TabsContent value="list" className="mt-0 pr-4">
                  {/* Entry Node Change */}
                  <EntryNodeChange entryNode={diff.entry_node} nodeLabels={nodeLabels} />
                  
                  {/* Node Changes - Grouped */}
                  {diff.node_changes?.length > 0 && (
                    <div className="space-y-4">
                      {/* Added */}
                      {diff.node_changes.filter(n => n.change_type === 'added').length > 0 && (
                        <div>
                          <h3 className="text-xs font-medium text-emerald-400 mb-2 flex items-center gap-2">
                            <Plus className="h-3 w-3" />
                            Steps Added ({diff.node_changes.filter(n => n.change_type === 'added').length})
                          </h3>
                          {diff.node_changes
                            .filter(n => n.change_type === 'added')
                            .map(change => (
                              <NodeChangeCard
                                key={change.node_id}
                                change={change}
                                isExpanded={expandedNodes.has(change.node_id)}
                                onToggle={() => toggleNode(change.node_id)}
                              />
                            ))}
                        </div>
                      )}
                      
                      {/* Modified */}
                      {diff.node_changes.filter(n => n.change_type === 'modified').length > 0 && (
                        <div>
                          <h3 className="text-xs font-medium text-amber-400 mb-2 flex items-center gap-2">
                            <Pencil className="h-3 w-3" />
                            Steps Modified ({diff.node_changes.filter(n => n.change_type === 'modified').length})
                          </h3>
                          {diff.node_changes
                            .filter(n => n.change_type === 'modified')
                            .map(change => (
                              <NodeChangeCard
                                key={change.node_id}
                                change={change}
                                isExpanded={expandedNodes.has(change.node_id)}
                                onToggle={() => toggleNode(change.node_id)}
                              />
                            ))}
                        </div>
                      )}
                      
                      {/* Removed */}
                      {diff.node_changes.filter(n => n.change_type === 'removed').length > 0 && (
                        <div>
                          <h3 className="text-xs font-medium text-red-400 mb-2 flex items-center gap-2">
                            <Minus className="h-3 w-3" />
                            Steps Removed ({diff.node_changes.filter(n => n.change_type === 'removed').length})
                          </h3>
                          {diff.node_changes
                            .filter(n => n.change_type === 'removed')
                            .map(change => (
                              <NodeChangeCard
                                key={change.node_id}
                                change={change}
                                isExpanded={expandedNodes.has(change.node_id)}
                                onToggle={() => toggleNode(change.node_id)}
                              />
                            ))}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Edge Changes */}
                  {diff.edge_changes?.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-white/5">
                      <h3 className="text-xs font-medium text-indigo-400 mb-2 flex items-center gap-2">
                        <GitBranch className="h-3 w-3" />
                        Route Changes ({diff.edge_changes.length})
                      </h3>
                      {diff.edge_changes.map(change => (
                        <EdgeChangeCard 
                          key={change.edge_id} 
                          change={change} 
                          nodeLabels={nodeLabels}
                        />
                      ))}
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="sidebyside" className="mt-0 pr-4">
                  {diff.node_changes?.filter(n => n.change_type === 'modified').length > 0 ? (
                    <div className="space-y-6">
                      {diff.node_changes
                        .filter(n => n.change_type === 'modified')
                        .map(change => (
                          <div key={change.node_id}>
                            <div className="flex items-center gap-2 mb-2">
                              <Pencil className="h-3.5 w-3.5 text-amber-400" />
                              <span className="text-sm font-medium text-slate-200">
                                {change.label || change.node_id}
                              </span>
                              <Badge variant="outline" className="text-[10px]">
                                {NODE_TYPE_CONFIG[change.node_type]?.label || change.node_type}
                              </Badge>
                            </div>
                            <SideBySideCompare before={change.before} after={change.after} />
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-48 text-slate-500">
                      <CircleDot className="h-5 w-5 mr-2" />
                      No modified steps to compare
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="graph" className="mt-0 pr-4">
                  <GraphOverlay diff={diff} nodeLabels={nodeLabels} />
                </TabsContent>
              </ScrollArea>
            </Tabs>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Inline Compare Button
// =============================================================================

export function CompareVersionButton({ 
  funnelId, 
  baseVersion, 
  compareVersion,
  className,
}) {
  const [open, setOpen] = useState(false);
  
  return (
    <VersionCompareDialog
      funnelId={funnelId}
      baseVersion={baseVersion}
      compareVersion={compareVersion}
      open={open}
      onOpenChange={setOpen}
      trigger={
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "h-6 px-2 text-xs text-slate-400 hover:text-slate-200",
            className
          )}
          data-testid={`compare-btn-v${baseVersion}-v${compareVersion}`}
        >
          <GitCompare className="h-3 w-3 mr-1" />
          Diff
        </Button>
      }
    />
  );
}

export default VersionCompareDialog;
